import { Alert, Box, Button, Card, Snackbar, TextField, Typography } from '@mui/material';
import axios from 'axios';
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { setAuthToken } from './Hooks/setAuthToken';
import history from './Routes/history';
import { BeforeInstallPromptEvent, LoginResponse } from './Types/Types';
import { buamatogVerificationKey } from './Types/Utility';

interface Props {

}

class Login extends Component<{}, { errors: { username: boolean, password: boolean }, errorMsg: string}> {
    constructor(props : Props) {
        super(props);
        
        this.state={
            errors: {
                username: false,
                password: false,
            },
            errorMsg: "",
        }
    }

    componentDidMount() {
        let isTooSoon = true;
        window.addEventListener("beforeinstallprompt", (e : BeforeInstallPromptEvent ) => {
            if (isTooSoon) {
                e.preventDefault(); // Prevents prompt display
                // Prompt later instead:
                setTimeout(() => {
                    isTooSoon = false;
                    e.prompt(); // Throws if called more than once or default not prevented
                }, 10000);
            }
            // The event was re-dispatched in response to our request
            // …
        });

        const buamatogkey = buamatogVerificationKey();
        const token = localStorage.getItem(buamatogkey);
        const overwrite = localStorage.getItem('buamatog-is-wonn-i-sog-2025');
        localStorage.clear();
        if (token) {
            localStorage.setItem(buamatogkey, token);
            if (overwrite) {
                localStorage.setItem('buamatog-is-wonn-i-sog-2025', overwrite);
            }
        }
    }
    
    hasErrors = (name?: string) => {
        const { errors } = this.state;

        if (name) {
            switch (name) {
                case 'username':
                    return errors.username;
                case 'password':
                    return errors.password;
            }
        }
        return errors.username || errors.password;
    }

    check = (username : string, password : string) => {
        const _self = this;
        return new Promise((resolve, reject) => {
            let hasErrors = false;
            let errors = {
                username: false,
                password: false,
            };
            if (username.length < 4) {
                errors.username = true;
                hasErrors = true;
            }

            if (password.length < 8) {
                errors.password = true;
                hasErrors = true;
            }

            _self.setState({
                errors: errors
            })

            if (hasErrors) {
                reject(false);
                return false;
            }
            resolve(true);
            return true;
        });
    }

    handleSubmit = (e : React.SyntheticEvent) => {
        e.preventDefault();
        const target = e.target as typeof e.target & {
            username: { value: string };
            password: { value: string };
        };
        const username = target.username.value; // typechecks!
        const password = target.password.value; // typechecks!
        
        const _self = this;
        _self.check(username, password)
            .then(res => {
                if (!_self.hasErrors()) {
                    axios.post<LoginResponse>('/login', {
                            username: username,
                            password: password,
                        })
                        .then(res => {
                            setAuthToken(res.data.token);
                            history.push('/');
                        })
                        .catch(err => {
                            this.setState({
                                errors: {
                                    username: true,
                                    password: true,
                                },
                                errorMsg: 'Benutzername oder Passwort falsch'
                            });
                            setAuthToken(null);
                        });
                }
            })

        return false;
    }

    handleCloseError = () => {
        this.setState({
            errorMsg: "",
        });
    }

    render() {
        const { errorMsg } = this.state;

        const inputProps = {
            fullWidth: true,
            sx: { display: 'block', m: 1, width: 'calc(100% - 20px)' }
        };

        return (
            <React.Fragment>
                <Snackbar open={errorMsg !== ""} autoHideDuration={6000} onClose={() => this.handleCloseError()} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={() => this.handleCloseError()} severity="error" sx={{ width: '100%' }}>
                        {errorMsg}
                    </Alert>
                </Snackbar>
                <Box 
                    flexGrow={1} 
                    sx={{ 
                        overflowY: 'auto',
                        position: 'relative',
                        minHeight: '100vh',
                        background: 'linear-gradient(135deg, #181818 0%, #232323 50%, #E2B13C 100%)',
                    }}
                >
                    {/* Wave Background */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            overflow: 'hidden',
                            lineHeight: 0,
                            transform: 'rotate(180deg)',
                            zIndex: 0,
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1200 120"
                            preserveAspectRatio="none"
                            style={{ width: '100%', height: '150px' }}
                        >
                            <path
                                d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                                style={{ fill: '#ffffff', opacity: 0.3 }}
                            />
                            <path
                                d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
                                style={{ fill: '#ffffff', opacity: 0.5 }}
                            />
                        </svg>
                    </Box>

                    {/* Content */}
                    <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center', py: 4 }}>
                        <Card 
                            sx={{ 
                                background: 'rgba(35, 35, 35, 0.8)',
                                backdropFilter: 'blur(12px)',
                                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
                                borderRadius: '16px',
                                border: '1px solid rgba(255, 215, 0, 0.18)',
                                maxWidth: '500px',
                                width: '90%',
                                p: 3,
                            }}
                        >
                            <Box component={"form"} autoComplete={"off"} noValidate onSubmit={this.handleSubmit} sx={{ width: '100%' }}>
                                <Box sx={{ p: 2 }}>
                                    <img 
                                        src={process.env.PUBLIC_URL + '/LogoBuamatog_nobg_224x204.png'} 
                                        alt="Buamatog Logo"
                                        style={{ maxHeight: '25vh', maxWidth: '100%' }}
                                    />
                                </Box>
                                <Box sx={{ p: 2 }}>
                                    <Typography variant={'h4'} sx={{ fontWeight: 600, color: '#FFFFFF' }}>Buamatog Login</Typography>
                                    <Typography variant={'body1'} sx={{ color: '#CCCCCC', mt: 1 }}>Des zeug vom letztn johr geht nimma!</Typography>
                                </Box>
                                <Box>
                                    <TextField id="username" label="Username" name="username" error={this.hasErrors("username")} variant="outlined" required={true} {...inputProps}/>
                                    <TextField id="password" type="password" label="Passwort" name="password" error={this.hasErrors("password")} variant="outlined" required={true} {...inputProps}/>
                                </Box>
                                <Box sx={{ display: 'grid', placeItems: 'center', width: '100%', textAlign: 'center', mt: 2 }}>
                                    <Button type={"submit"} variant="contained" size="large" sx={{ minWidth: '200px' }}>
                                        Login
                                    </Button>
                                </Box>
                                <Box sx={{ display: 'grid', placeItems: 'center', width: '100%', textAlign: 'center', mt: 1 }}>
                                    <Button component={Link} to={"/register"}>Registrieren</Button>
                                </Box>
                            </Box>
                        </Card>
                    </Box>
                </Box>
            </React.Fragment>
        );
    }
}
export default Login;